import type {
  AuditEntry,
  Avatar,
  ChatHistory,
  CreatedInvite,
  HistoryEntry,
  Invite,
  Me,
  MediaSearchResult,
  PlaylistDetail,
  PlaylistItem,
  PlaylistSummary,
  PublicProfile,
  PublicUser,
  RandomRoomResult,
  Report,
  RoomDetail,
  RoomList,
  RoomSummary,
  SessionInfo,
  Video,
} from '@crowdset/contracts';
import { API } from '@crowdset/contracts/constants';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api, setCsrfToken } from './api';

export const qk = {
  session: ['session'] as const,
  rooms: (q: string, sort: string) => ['rooms', q, sort] as const,
  room: (slug: string) => ['room', slug] as const,
  favorites: ['favorites'] as const,
  ownedRooms: ['owned-rooms'] as const,
  avatars: ['avatars'] as const,
  playlists: ['playlists'] as const,
  playlist: (id: string) => ['playlist', id] as const,
  invites: (slug: string) => ['invites', slug] as const,
  history: (slug: string) => ['history', slug] as const,
  profile: (h: string) => ['profile', h] as const,
  devUsers: ['dev-users'] as const,
  reports: (status: string) => ['admin-reports', status] as const,
  audit: (roomId: string) => ['admin-audit', roomId] as const,
};

export async function fetchSession(): Promise<SessionInfo> {
  const s = await api.get<SessionInfo>(API.session);
  setCsrfToken(s.csrfToken);
  return s;
}

export function useSession() {
  return useQuery({ queryKey: qk.session, queryFn: fetchSession, staleTime: 60_000, retry: 1 });
}

/** Convenience: the signed-in, verified user (null for guests). */
export function useMe(): Me | null {
  return useSession().data?.user ?? null;
}

export function useRooms(q: string, sort: 'active' | 'new' | 'name') {
  return useInfiniteQuery({
    queryKey: qk.rooms(q, sort),
    queryFn: ({ pageParam, signal }) =>
      api.get<RoomList>(
        API.rooms,
        { q: q || undefined, sort, cursor: pageParam ?? undefined, limit: 24 },
        signal,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    refetchInterval: 20_000,
  });
}

export function useRoom(slug: string) {
  return useQuery({
    queryKey: qk.room(slug),
    queryFn: () => api.get<RoomDetail>(API.room(slug)),
    retry: false,
  });
}

export function useFavorites(enabled: boolean) {
  return useQuery({
    queryKey: qk.favorites,
    queryFn: () => api.get<RoomSummary[]>(API.meFavorites),
    enabled,
  });
}

export function useOwnedRooms(enabled: boolean) {
  return useQuery({
    queryKey: qk.ownedRooms,
    queryFn: () => api.get<RoomSummary[]>(API.meOwnedRooms),
    enabled,
  });
}

export function useAvatars() {
  return useQuery({
    queryKey: qk.avatars,
    queryFn: () => api.get<Avatar[]>(API.avatars),
    staleTime: Infinity,
  });
}

export function usePlaylists(enabled: boolean) {
  return useQuery({
    queryKey: qk.playlists,
    queryFn: () => api.get<PlaylistSummary[]>(API.playlists),
    enabled,
  });
}

export function usePlaylist(id: string | null) {
  return useQuery({
    queryKey: qk.playlist(id ?? 'none'),
    queryFn: () => api.get<PlaylistDetail>(API.playlist(id!)),
    enabled: !!id,
  });
}

export function useInvites(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: qk.invites(slug),
    queryFn: () => api.get<Invite[]>(API.roomInvites(slug)),
    enabled,
  });
}

export function useHistory(slug: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: qk.history(slug),
    queryFn: ({ pageParam }) =>
      api.get<{ items: HistoryEntry[]; nextCursor: string | null }>(API.roomHistory(slug), {
        cursor: pageParam ?? undefined,
        limit: 20,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  });
}

export function useProfile(handle: string) {
  return useQuery({
    queryKey: qk.profile(handle),
    queryFn: () => api.get<PublicProfile>(API.profile(handle)),
  });
}

export function useDevUsers(enabled: boolean) {
  return useQuery({
    queryKey: qk.devUsers,
    queryFn: () => api.get<PublicUser[]>(API.devUsers),
    enabled,
  });
}

export function useAdminReports(status: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: qk.reports(status),
    queryFn: ({ pageParam }) =>
      api.get<{ items: Report[]; nextCursor: string | null }>(API.adminReports, {
        status,
        cursor: pageParam ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  });
}

export function useAudit(roomId: string, enabled: boolean) {
  return useQuery({
    queryKey: qk.audit(roomId),
    queryFn: () =>
      api.get<{ items: AuditEntry[]; nextCursor: string | null }>(API.adminAudit, {
        room: roomId || undefined,
      }),
    enabled,
  });
}

export const fetchRandomRoom = () => api.get<RandomRoomResult>(API.randomRoom);
export const lookupMedia = (input: string) => api.get<Video>(API.mediaLookup, { input });
export const searchMedia = (q: string, pageToken?: string) =>
  api.get<MediaSearchResult>(API.mediaSearch, { q, pageToken });
export const fetchChatBefore = (slug: string, before: string) =>
  api.get<ChatHistory>(API.roomChat(slug), { before });

/* ------------------------------------------------------------------ mutations */

export function usePlaylistMutations() {
  const qc = useQueryClient();
  const refresh = (id?: string) => {
    void qc.invalidateQueries({ queryKey: qk.playlists });
    if (id) void qc.invalidateQueries({ queryKey: qk.playlist(id) });
  };
  return {
    create: useMutation({
      mutationFn: (name: string) => api.post<PlaylistDetail>(API.playlists, { name }),
      onSuccess: (p) => {
        refresh(p.id);
        // A first playlist becomes active automatically.
        void qc.invalidateQueries({ queryKey: qk.session });
      },
    }),
    rename: useMutation({
      mutationFn: ({ id, name }: { id: string; name: string }) =>
        api.patch<PlaylistDetail>(API.playlist(id), { name }),
      onSuccess: (_d, v) => refresh(v.id),
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.del<void>(API.playlist(id)),
      onSuccess: () => {
        refresh();
        void qc.invalidateQueries({ queryKey: qk.session });
      },
    }),
    addItem: useMutation({
      mutationFn: ({
        id,
        input,
        position,
      }: {
        id: string;
        input: string;
        position?: 'top' | 'bottom';
      }) =>
        api.post<PlaylistItem>(API.playlistItems(id), { input, position: position ?? 'bottom' }),
      onSuccess: (_d, v) => refresh(v.id),
    }),
    removeItem: useMutation({
      mutationFn: ({ id, itemId }: { id: string; itemId: string }) =>
        api.del<void>(API.playlistItem(id, itemId)),
      onSuccess: (_d, v) => refresh(v.id),
    }),
    moveItem: useMutation({
      mutationFn: ({ id, itemId, toIndex }: { id: string; itemId: string; toIndex: number }) =>
        api.post<PlaylistDetail>(API.playlistItemMove(id, itemId), { toIndex }),
      onMutate: async ({ id, itemId, toIndex }) => {
        await qc.cancelQueries({ queryKey: qk.playlist(id) });
        const prev = qc.getQueryData<PlaylistDetail>(qk.playlist(id));
        if (prev) {
          const items = [...prev.items];
          const from = items.findIndex((i) => i.id === itemId);
          if (from >= 0) {
            const [it] = items.splice(from, 1);
            items.splice(toIndex, 0, it!);
            qc.setQueryData(qk.playlist(id), {
              ...prev,
              items: items.map((i, n) => ({ ...i, position: n })),
            });
          }
        }
        return { prev };
      },
      onError: (_e, v, ctx) => {
        if (ctx?.prev) qc.setQueryData(qk.playlist(v.id), ctx.prev);
      },
      onSettled: (_d, _e, v) => refresh(v.id),
    }),
    setActive: useMutation({
      mutationFn: (playlistId: string | null) =>
        api.put<void>(API.meActivePlaylist, { playlistId }),
      onSuccess: () => {
        refresh();
        void qc.invalidateQueries({ queryKey: qk.session });
      },
    }),
  };
}

export function useFavoriteToggle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, on }: { roomId: string; on: boolean }) =>
      on ? api.put<void>(API.meFavorite(roomId)) : api.del<void>(API.meFavorite(roomId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.favorites });
      void qc.invalidateQueries({ queryKey: ['rooms'] });
      void qc.invalidateQueries({ queryKey: ['room'] });
    },
  });
}

export function useCreateInvite(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { expiresInHours: number; maxUses: number | null }) =>
      api.post<CreatedInvite>(API.roomInvites(slug), body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.invites(slug) }),
  });
}

export function useRevokeInvite(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(API.roomInvite(slug, id)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.invites(slug) }),
  });
}
